
import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Icon,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import RefreshIcon from "@mui/icons-material/Refresh";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PaymentsIcon from "@mui/icons-material/Payments";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AssessmentIcon from "@mui/icons-material/Assessment";

import apiClient from "../BaseUrl/apiClient.js";
import TokenService from "../service/TokenService";
import SriyanshCardHeader from "../components/CardHeader";
import WhatsAppButton from "../whatsapp/WhatsAppButton.js";
import PoReport from "../masterpages/Reports/Po/PoReport";


// ============================================================
// DASHBOARD ITEMS
// ============================================================

const DASHBOARD_ITEMS = [
  {
    key: "po",
    title: "Pending PO",
    subtitle: "Purchase Orders",
    reportType: 1,
    reportHeading: "Pending PO",
    icon: ShoppingCartIcon,
    gradient: "linear-gradient(135deg,#00695c,#26a69a)",
    softBackground: "#e0f2f1",
    iconColor: "#00695c",
  },
  {
    key: "notreceived",
    title: "Material Receiving",
    subtitle: "Material pending",
    reportType: 2,
    reportHeading: "Materials Receiving Pending",
    icon: Inventory2Icon,
    gradient: "linear-gradient(135deg,#2e7d32,#66bb6a)",
    softBackground: "#e8f5e9",
    iconColor: "#2e7d32",
  },
  {
    key: "suitability",
    title: "Suitability Pending",
    subtitle: "Awaiting suitability",
    reportType: 3,
    reportHeading: "Suitability Pending",
    icon: FactCheckIcon,
    gradient: "linear-gradient(135deg,#c62828,#ef5350)",
    softBackground: "#ffebee",
    iconColor: "#c62828",
  },
  {
    key: "billpending",
    title: "Bill Receiving",
    subtitle: "Bill pending from firm",
    reportType: 4,
    reportHeading: "Bill Receiving Pending from Firm",
    icon: ReceiptLongIcon,
    gradient: "linear-gradient(135deg,#ef6c00,#ffb74d)",
    softBackground: "#fff3e0",
    iconColor: "#ef6c00",
  },
  {
    key: "cospending",
    title: "Bill Passing",
    subtitle: "Passing / funding pending",
    reportType: 5,
    reportHeading: "Bill Passing Pending",
    icon: AccountBalanceIcon,
    gradient: "linear-gradient(135deg,#6d4c41,#a1887f)",
    softBackground: "#efebe9",
    iconColor: "#6d4c41",
  },
  {
    key: "accountpending",
    title: "CRN Pending",
    subtitle: "Accounts pending",
    reportType: 6,
    reportHeading: "CRN Pending",
    icon: CreditCardIcon,
    gradient: "linear-gradient(135deg,#263238,#546e7a)",
    softBackground: "#eceff1",
    iconColor: "#263238",
  },
  {
    key: "paymentpending",
    title: "Payment Pending",
    subtitle: "Awaiting payment",
    reportType: 7,
    reportHeading: "Payment Pending",
    icon: PaymentsIcon,
    gradient: "linear-gradient(135deg,#880e4f,#ec407a)",
    softBackground: "#fce4ec",
    iconColor: "#880e4f",
  },
  {
    key: "paid",
    title: "Payment Done",
    subtitle: "Successfully paid",
    reportType: 8,
    reportHeading: "Paid",
    icon: CheckCircleIcon,
    gradient: "linear-gradient(135deg,#1565c0,#42a5f5)",
    softBackground: "#e3f2fd",
    iconColor: "#1565c0",
  },
];


// ============================================================
// PAGE
// ============================================================

function PgHome() {
  const tokenServiceDetails = TokenService.getUserDetails();

  const uname = tokenServiceDetails?.uname;

  const reportRef = useRef(null);

  const [dashboardData, setDashboardData] = useState({
    po: 0,
    notreceived: 0,
    suitability: 0,
    billpending: 0,
    cospending: 0,
    accountpending: 0,
    paymentpending: 0,
    paid: 0,
  });

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [generateReportClick, setGenerateReportClick] = useState(false);
  const [report, setReport] = useState("");
  const [reportHead, setReportHead] = useState("");


  // ============================================================
  // LOAD DASHBOARD DATA
  // ============================================================

  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await apiClient.post("/getdashboarddata", {
        uname,
      });

      const data = response?.data?.[0] || {};

      setDashboardData({
        po: Number(data.po || 0),
        notreceived: Number(data.notreceived || 0),
        suitability: Number(data.suitability || 0),
        billpending: Number(data.billpending || 0),
        cospending: Number(data.cospending || 0),
        accountpending: Number(data.accountpending || 0),
        paymentpending: Number(data.paymentpending || 0),
        paid: Number(data.paid || 0),
      });
    } catch (error) {
      console.error("Dashboard data error:", error);
    } finally {
      setLoading(false);
    }
  }, [uname]);


  // ============================================================
  // INITIAL LOAD
  // ============================================================

  useEffect(() => {
    getData();
  }, [getData]);


  // ============================================================
  // REFRESH
  // ============================================================

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await getData();
    } finally {
      setRefreshing(false);
    }
  };


  // ============================================================
  // REPORT
  // ============================================================

  const pending = (reportType, reportHeading) => {
    setReport(reportType);
    setReportHead(reportHeading);
    setGenerateReportClick(true);

    setTimeout(() => {
      reportRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };


  const clearReport = () => {
    setReport("");
    setReportHead("");
    setGenerateReportClick(false);
  };


  const reportData = {
    consignee: "Select All",
    reporttype: report,
    reportsubtype: "",
    reportHeading: reportHead,
  };


  // ============================================================
  // SUMMARY
  // ============================================================

  const totalPending =
    dashboardData.po +
    dashboardData.notreceived +
    dashboardData.suitability +
    dashboardData.billpending +
    dashboardData.cospending +
    dashboardData.accountpending +
    dashboardData.paymentpending;

  const totalCompleted = dashboardData.paid;


  return (
    <>
      {/* ========================================================
          HEADER
      ======================================================== */}

      <Card
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid #e1e7ef",
          background: "#ffffff",
        }}
      >
        <Box
          sx={{
            background:
              "linear-gradient(135deg,#0d47a1 0%,#1565c0 45%,#42a5f5 100%)",
            color: "#fff",
            px: {
              xs: 2,
              sm: 3,
              md: 4,
            },
            py: {
              xs: 2,
              sm: 2.5,
              md: 3,
            },
          }}
        >
          <Stack
  direction="row"
  alignItems="center"
  justifyContent="space-between"
  spacing={2}
  sx={{
    width: "100%",
    minWidth: 0,
  }}
>
  {/* LEFT SIDE */}

  <Stack
    direction="row"
    spacing={1.5}
    alignItems="center"
    sx={{
      minWidth: 0,
      flex: 1,
    }}
  >
    {/* <Box
      sx={{
        width: {
          xs: 44,
          sm: 52,
        },
        height: {
          xs: 44,
          sm: 52,
        },
        minWidth: {
          xs: 44,
          sm: 52,
        },

        borderRadius: 2.5,

        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        background: "rgba(255,255,255,.18)",
        backdropFilter: "blur(8px)",
        border: "1px solid rgba(255,255,255,.25)",
      }}
    >
      <DashboardIcon
        sx={{
          fontSize: {
            xs: 25,
            sm: 30,
          },
        }}
      />
    </Box> */}

    <Box
      sx={{
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: {
            xs: "1.05rem",
            sm: "1.35rem",
          },

          fontWeight: 900,
          lineHeight: 1.2,

          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        Main Dashboard
      </Typography>

      <Typography
        sx={{
          mt: 0.4,
          opacity: 0.88,

          fontSize: {
            xs: "0.76rem",
            sm: "0.88rem",
          },

          whiteSpace: {
            xs: "normal",
            sm: "nowrap",
          },
        }}
      >
        Purchase Order & Payment Management
      </Typography>
    </Box>
  </Stack>

  {/* REFRESH BUTTON */}

  <Tooltip title="Refresh dashboard">
    <IconButton
      onClick={handleRefresh}
      disabled={refreshing}
      sx={{
        flexShrink: 0,

        width: {
          xs: 40,
          sm: 44,
        },

        height: {
          xs: 40,
          sm: 44,
        },

        color: "#fff",

        background: "rgba(255,255,255,.14)",

        border: "1px solid rgba(255,255,255,.22)",

        "&:hover": {
          background: "rgba(255,255,255,.25)",
        },
      }}
    >
      {refreshing ? (
        <CircularProgress
          size={21}
          sx={{
            color: "#fff",
          }}
        />
      ) : (
        <RefreshIcon />
      )}
    </IconButton>
  </Tooltip>
</Stack>
        </Box>
      </Card>


     


      {/* ========================================================
          WORKFLOW SECTION
      ======================================================== */}

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid #dfe5ec",
          background: "#fff",
          overflow: "hidden",
        }}
      >

        {/* SECTION HEADER */}

        <Box
          sx={{
            px: {
              xs: 2,
              sm: 2.5,
              md: 3,
            },
            py: 2,
            background:
              "linear-gradient(90deg,#f5f9ff 0%,#ffffff 75%)",
            borderBottom: "1px solid #e5e9ef",
          }}
        >
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1}
            alignItems={{
              xs: "flex-start",
              sm: "center",
            }}
            justifyContent="space-between"
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 900,
                  color: "#1565c0",
                  fontSize: "1.1rem",
                }}
              >
                Purchase & Payment Workflow
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Click any card to view the detailed report
              </Typography>
            </Box>

            <Box
              sx={{
                px: 1.5,
                py: 0.7,
                borderRadius: 2,
                backgroundColor: "#e3f2fd",
                color: "#1565c0",
                fontSize: "0.78rem",
                fontWeight: 800,
              }}
            >
              {DASHBOARD_ITEMS.length} Modules
            </Box>
          </Stack>
        </Box>


        {/* CARDS */}

        <Box
          sx={{
            p: {
              xs: 1.5,
              sm: 2,
              md: 2.5,
            },
          }}
        >
          {loading ? (
            <Box
              sx={{
                minHeight: 300,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <CircularProgress />

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Loading dashboard...
              </Typography>
            </Box>
          ) : (
           <Box
  className="dashboard-workflow-grid"
  sx={{
    display: "grid",

    gridTemplateColumns: {
      xs: "1fr",
      sm: "repeat(2, minmax(0, 1fr))",
      md: "repeat(3, minmax(0, 1fr))",
      lg: "repeat(4, minmax(0, 1fr))",
    },

    gap: {
      xs: 1.5,
      sm: 2,
      md: 2.2,
    },

    width: "100%",
    alignItems: "stretch",
  }}
>
  {DASHBOARD_ITEMS.map((item) => (
    <Box
      key={item.key}
      sx={{
        minWidth: 0,
        width: "100%",
        display: "flex",
      }}
    >
      <WorkflowCard
        item={item}
        value={dashboardData[item.key]}
        onClick={() =>
          pending(
            item.reportType,
            item.reportHeading
          )
        }
      />
    </Box>
  ))}
</Box>
          )}
        </Box>
      </Paper>

 {/* ========================================================
          SUMMARY CARDS
      ======================================================== */}

      {/* <Grid
        container
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <SummaryCard
            title="Total Pending"
            value={totalPending}
            subtitle="Items requiring attention"
            icon={<AssessmentIcon />}
            background="#fff8f8"
            iconBackground="#ffebee"
            iconColor="#c62828"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <SummaryCard
            title="Payment Completed"
            value={totalCompleted}
            subtitle="Completed payments"
            icon={<CheckCircleIcon />}
            background="#f5fbf6"
            iconBackground="#c8e6c9"
            iconColor="#2e7d32"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 12, md: 4 }}>
          <SummaryCard
            title="Dashboard Modules"
            value={DASHBOARD_ITEMS.length}
            subtitle="Available workflow modules"
            icon={<DashboardIcon />}
            background="#f7fbff"
            iconBackground="#e3f2fd"
            iconColor="#1565c0"
          />
        </Grid>
      </Grid> */}
      {/* ========================================================
          REPORT
      ======================================================== */}

      {generateReportClick && (
        <Box
          ref={reportRef}
          sx={{
            mt: 3,
            scrollMarginTop: 20,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid #dfe5ec",
              background: "#fff",
            }}
          >

            {/* REPORT HEADER */}

            <Box
              sx={{
                px: {
                  xs: 2,
                  sm: 2.5,
                  md: 3,
                },
                py: 2,
                background:
                  "linear-gradient(90deg,#f5f9ff,#ffffff)",
                borderBottom: "1px solid #e5e9ef",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Stack
                  direction="row"
                  spacing={1.2}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#e3f2fd",
                      color: "#1565c0",
                    }}
                  >
                    <AssessmentIcon />
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 900,
                        color: "#1565c0",
                      }}
                    >
                      {reportHead}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      Detailed report
                    </Typography>
                  </Box>
                </Stack>

                <Tooltip title="Close report">
                  <IconButton
                    onClick={clearReport}
                    size="small"
                    sx={{
                      backgroundColor: "#ffebee",
                      color: "#c62828",
                      "&:hover": {
                        backgroundColor: "#ffcdd2",
                      },
                    }}
                  >
                    ✕
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            {/* REPORT */}

            <Box sx={{ p: { xs: 1, sm: 2 } }}>
              <PoReport
                consignee={reportData.consignee}
                reporttype={reportData.reporttype}
                reportsubtype={reportData.reportsubtype}
                heading={reportHead}
              />
            </Box>
          </Paper>
        </Box>
      )}


      {/* ========================================================
          WHATSAPP
      ======================================================== */}

      <WhatsAppButton />
    </>
  );
}


// ============================================================
// WORKFLOW CARD
// ============================================================

function WorkflowCard({ item, value, onClick }) {
  const Icon = item.icon;
  const hasData = Number(value || 0) > 0;

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        width: "100%",
        height: "100%",
        minHeight: {
          xs: 165,
          sm: 190,
          md: 195,
        },

        display: "flex",
        flexDirection: "column",

        borderRadius: {
          xs: 2.5,
          sm: 3,
        },

        overflow: "hidden",
        cursor: "pointer",

        border: "1px solid",
        borderColor: hasData ? "#d9e2ec" : "#e6e9ed",

        background: "#ffffff",

        boxSizing: "border-box",

        transition:
          "transform .25s ease, box-shadow .25s ease, border-color .25s ease",

        "&:hover": {
          transform: {
            xs: "none",
            sm: "translateY(-5px)",
          },

          boxShadow: {
            xs: "none",
            sm: "0 14px 32px rgba(0,0,0,.11)",
          },

          borderColor: item.iconColor,
        },

        "&:active": {
          transform: "scale(.99)",
        },
      }}
    >
      {/* TOP COLOR STRIP */}
      <Box
        sx={{
          height: 6,
          width: "100%",
          flexShrink: 0,
          background: item.gradient,
        }}
      />

      {/* CARD CONTENT */}
      <Box
        sx={{
          p: {
            xs: 1.5,
            sm: 2,
            md: 2.1,
          },

          flex: 1,

          display: "flex",
          flexDirection: "column",

          minWidth: 0,
        }}
      >
        {/* TITLE ROW */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{
            minWidth: 0,
          }}
        >
          {/* ICON + TITLE */}
          <Stack
            direction="row"
            spacing={1.2}
            alignItems="center"
            sx={{
              minWidth: 0,
              flex: 1,
            }}
          >
            {/* ICON */}
            <Box
              sx={{
                width: {
                  xs: 42,
                  sm: 46,
                },

                height: {
                  xs: 42,
                  sm: 46,
                },

                minWidth: {
                  xs: 42,
                  sm: 46,
                },

                borderRadius: 2.2,

                display: "flex",
                alignItems: "center",
                justifyContent: "center",

                backgroundColor: item.softBackground,
                color: item.iconColor,
              }}
            >
              <Icon
                sx={{
                  fontSize: {
                    xs: 21,
                    sm: 24,
                  },
                }}
              />
            </Box>

            {/* TITLE */}
            <Box
              sx={{
                minWidth: 0,
                flex: 1,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 850,

                  fontSize: {
                    xs: "0.88rem",
                    sm: "0.94rem",
                  },

                  lineHeight: 1.2,

                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",

                  overflow: "hidden",

                  wordBreak: "break-word",
                }}
              >
                {item.title}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",

                  mt: 0.3,

                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",

                  fontSize: {
                    xs: "0.7rem",
                    sm: "0.74rem",
                  },
                }}
              >
                {item.subtitle}
              </Typography>
            </Box>
          </Stack>

          {/* ARROW */}
          <ArrowForwardIcon
            sx={{
              fontSize: 19,
              color: "#9aa5b1",
              flexShrink: 0,
            }}
          />
        </Stack>

        {/* NUMBER */}
        <Box
          sx={{
            mt: {
              xs: 2,
              sm: 2.5,
            },
          }}
        >
          <Typography
            sx={{
              fontSize: {
                xs: "1.8rem",
                sm: "2rem",
                md: "2.1rem",
              },

              lineHeight: 1,

              fontWeight: 900,

              color: item.iconColor,
            }}
          >
            {Number(value || 0).toLocaleString("en-IN")}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: {
                xs: "0.68rem",
                sm: "0.72rem",
              },
            }}
          >
            {hasData
              ? "items requiring attention"
              : "no pending items"}
          </Typography>
        </Box>

        {/* BOTTOM STATUS */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            mt: "auto",

            pt: 1.3,

            borderTop: "1px solid #edf0f3",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,

              color: hasData
                ? item.iconColor
                : "#2e7d32",

              fontSize: {
                xs: "0.7rem",
                sm: "0.74rem",
              },
            }}
          >
            {hasData ? "View Pending" : "All Clear"}
          </Typography>

          {hasData ? (
            <Box
              sx={{
                width: 8,
                height: 8,

                borderRadius: "50%",

                backgroundColor: item.iconColor,

                boxShadow: `0 0 0 4px ${item.softBackground}`,
              }}
            />
          ) : (
            <CheckCircleIcon
              sx={{
                fontSize: 18,
                color: "#2e7d32",
              }}
            />
          )}
        </Stack>
      </Box>
    </Paper>
  );
}

// ============================================================
// SUMMARY CARD
// ============================================================

function SummaryCard({
  title,
  value,
  subtitle,
  icon,
  background,
  iconBackground,
  iconColor,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        p: 2.2,
        borderRadius: 3,
        border: "1px solid #e1e6ec",
        background: background || "#fff",

        transition: "all .25s ease",

        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 10px 25px rgba(0,0,0,.08)",
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
      >

        <Box
          sx={{
            width: 50,
            height: 50,
            borderRadius: 2.2,

            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            backgroundColor: iconBackground,
            color: iconColor,

            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            fontWeight={700}
          >
            {title}
          </Typography>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              lineHeight: 1.1,
              mt: 0.3,
              color: iconColor || "#222",
            }}
          >
            {Number(value || 0).toLocaleString("en-IN")}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
          >
            {subtitle}
          </Typography>
        </Box>

      </Stack>
    </Paper>
  );
}


export default PgHome;

